// Epic / sub-issue breakdown UI strings. UI-locale plane only — the
// self-labelled send-texts the agent must quote live in
// `breakdown/breakdownMessage.ts` and stay byte-exact English (grounding
// plane, never localized).

import type { MsgSection } from "./types.js";

export const breakdown: MsgSection = {
  "breakdown.sizing.title": { en: "Size estimate", de: "Grössenschätzung" },
  "breakdown.sizing.files": {
    en: "~{count} files",
    de: "~{count} Dateien",
  },
  "breakdown.sizing.wait": {
    en: "Estimating implementation size…",
    de: "Umsetzungsgrösse wird geschätzt…",
  },
  "breakdown.split.recommended": {
    en: "This ticket looks big — splitting it is recommended.",
    de: "Dieses Ticket wirkt gross — eine Aufteilung wird empfohlen.",
  },
  "breakdown.split.forced": {
    en: "Splitting this ticket into sub-issues…",
    de: "Dieses Ticket wird in Sub-Issues aufgeteilt…",
  },
  "breakdown.split.cta": { en: "Split into sub-issues", de: "In Sub-Issues aufteilen" },
  "breakdown.split.force": { en: "Split anyway", de: "Trotzdem aufteilen" },
  "breakdown.split.backToProposal": {
    en: "Show me the split",
    de: "Aufteilung anzeigen",
  },
  "breakdown.split.notNeeded": {
    en: "This ticket looks small enough to build as one — but you can still split it.",
    de: "Dieses Ticket ist klein genug für ein Stück — du kannst es aber trotzdem aufteilen.",
  },
  "breakdown.split.oversizedSingle": {
    en: "This is large, but described as a single outcome. To split it, tell Loom the distinct pieces it should ship as.",
    de: "Das ist gross, aber als ein einziges Ergebnis beschrieben. Zum Aufteilen: Nenne Loom die einzelnen Teile, die ausgeliefert werden sollen.",
  },
  "breakdown.split.breakIntoPieces": {
    en: "Break it into pieces",
    de: "In Sub-Issues zerlegen",
  },
  "breakdown.epicBanner": {
    en: "Tracking epic — the work lives in {count} sub-issues below. Refine or implement those, not this issue.",
    de: "Tracking-Epic — die Arbeit steckt in den {count} Sub-Issues unten. Verfeinere oder implementiere diese, nicht dieses Issue.",
  },
  "breakdown.review.title": {
    en: "Proposed split",
    de: "Vorgeschlagene Aufteilung",
  },
  "breakdown.review.wait": {
    en: "Drafting the split…",
    de: "Aufteilung wird entworfen…",
  },
  "breakdown.review.revising": {
    en: "Revising the split…",
    de: "Aufteilung wird überarbeitet…",
  },
  // ---- leaner card (design-workflow winner) ----
  "breakdown.trust.covered": {
    en: "Nothing left out — every must-have is covered",
    de: "Nichts vergessen — jedes Muss-Kriterium ist abgedeckt",
  },
  // F13: once something is parked, "nothing left out" directly contradicts the
  // "Out of scope" list sitting right above it. This variant only ever renders
  // alongside that list, so it names what's actually true — every REMAINING
  // must-have is covered — instead of implying nothing was dropped.
  "breakdown.trust.coveredParked": {
    en: "Every remaining must-have is covered — {count} parked out of scope",
    de: "Jedes verbleibende Muss-Kriterium ist abgedeckt — {count} nicht im Umfang",
  },
  // Count-neutral on purpose: the warn branch fires whenever coveredCriteria <
  // totalCriteria, which can be short by more than one — "One must-have" would
  // mis-state a 2+ gap on the single line the user relies on. The required action
  // ("adjust") is the same regardless of count.
  "breakdown.trust.gap": {
    en: "A must-have isn't in a piece yet — adjust before creating",
    de: "Ein Muss-Kriterium ist noch in keinem Teil — vor dem Erstellen anpassen",
  },
  "breakdown.trust.tip": {
    en: "Every must-have goes into a piece — nothing is dropped.",
    de: "Jedes Muss-Kriterium landet in einem Teil — nichts geht verloren.",
  },
  "breakdown.trust.tipParked": {
    en: "Every must-have you kept goes into a piece — what's parked above is out on purpose, not dropped silently.",
    de: "Jedes behaltene Muss-Kriterium landet in einem Teil — was oben unter „Nicht im Umfang“ steht, ist bewusst draussen und nicht stillschweigend verloren.",
  },
  // The disclosure labels name the WORDING first, because that is what the
  // reveal now adds: the scope line renders whole in the resting row, so a
  // label pointing only at the criteria ("See what each delivers") gave a user
  // staring at a clamped sentence no reason to press it.
  "breakdown.reveal.delivers": {
    en: "Show every piece in full",
    de: "Jedes Teil vollständig zeigen",
  },
  "breakdown.reveal.hide": { en: "Hide details", de: "Details ausblenden" },
  "breakdown.reveal.rationale": { en: "Why these pieces?", de: "Warum diese Teile?" },
  "breakdown.child.delivers": {
    en: "Full wording & must-haves",
    de: "Ganzer Wortlaut & Muss-Kriterien",
  },
  // The build note the agent wrote for this piece. Labelled rather than shown
  // bare because Confirm authorises it: it is written into the sub-issue body,
  // and the user must be able to tell it apart from the scope line above it.
  "breakdown.child.approach": { en: "How it's built", de: "So wird's gebaut" },
  "breakdown.child.menu": { en: "Adjust this piece", de: "Diesen Teil anpassen" },
  "breakdown.child.splitFurther": { en: "Split this further", de: "Weiter aufteilen" },
  "breakdown.child.addPiece": { en: "Add a piece", de: "Teil hinzufügen" },

  // "+ Add a piece" used to open the same free-text box as "Adjust the split…":
  // an affordance promising a structured action, delivering a prompt box with no
  // hint what to type. `add_child` is a DETERMINISTIC op the server's reducer
  // executes — name, scope line, and the must-haves the new piece takes over —
  // so the surface asks for exactly those three and costs no model turn.
  "breakdown.add.title": { en: "Add a piece", de: "Teil hinzufügen" },
  "breakdown.add.help": {
    en: "Nothing new is invented: the must-haves you pick move off the pieces holding them now.",
    de: "Es wird nichts erfunden: Die gewählten Muss-Kriterien wandern von den Teilen weg, die sie jetzt halten.",
  },
  "breakdown.add.name": { en: "Name", de: "Name" },
  "breakdown.add.namePlaceholder": {
    en: "e.g. Export the log as CSV",
    de: "z. B. Log als CSV exportieren",
  },
  "breakdown.add.scope": { en: "What it delivers", de: "Was es liefert" },
  "breakdown.add.scopePlaceholder": {
    en: "One or two sentences someone outside the team would understand.",
    de: "Ein bis zwei Sätze, die auch jemand ausserhalb des Teams versteht.",
  },
  "breakdown.add.criteria": {
    en: "Which must-haves does it take over?",
    de: "Welche Muss-Kriterien übernimmt es?",
  },
  "breakdown.add.ownedBy": { en: "now in “{title}”", de: "jetzt in „{title}“" },
  "breakdown.add.criteriaRequired": {
    en: "Pick at least one must-have — a piece with nothing to deliver can’t be created.",
    de: "Wähle mindestens ein Muss-Kriterium — ein Teil ohne Lieferung lässt sich nicht erstellen.",
  },
  "breakdown.add.confirm": { en: "Add the piece", de: "Teil hinzufügen" },
  // Shown instead of the form: every piece already holds exactly one
  // must-have, so no pick can avoid stranding its current owner
  // (`AddChildDialog`'s own guard) — computable before the dialog opens, so the
  // button is disabled up front rather than after two filled fields and a tick.
  "breakdown.add.impossible": {
    en: "Every piece already holds exactly one must-have, so there’s nothing to move into a new one — split a piece first.",
    de: "Jeder Teil hat bereits genau ein Muss-Kriterium — es gibt also nichts, das sich in ein neues verschieben liesse. Teile zuerst einen Teil auf.",
  },

  "breakdown.adjust.open": { en: "Adjust the split…", de: "Aufteilung anpassen…" },
  "breakdown.adjust.describe": { en: "Describe a change…", de: "Änderung beschreiben…" },
  // Names what the click AUTHORISES, not just what it triggers: the pieces and
  // the wording shown are both written to the project, and the wording is the half a
  // user is least likely to realise they are signing off on.
  "breakdown.commitCaption": {
    en: "Creates {count} issues now, with the names and wording shown · no undo",
    de: "Erstellt jetzt {count} Issues — mit den gezeigten Namen und Formulierungen · kein Rückgängig",
  },
  "breakdown.confirmArm": {
    en: "Yes, create {count} — no undo",
    de: "Ja, {count} erstellen — kein Rückgängig",
  },
  "breakdown.confirmArmAria": {
    en: "Confirm again to create {count} sub-issues",
    de: "Erneut bestätigen, um {count} Sub-Issues zu erstellen",
  },
  // The armed step used to announce itself with a label swap alone, which reads
  // as nothing having happened — so a user who pressed once, looked away and
  // came back to the reverted label concluded the button was dead.
  "breakdown.confirmArmCaption": {
    en: "Press again to create them. This cannot be undone.",
    de: "Nochmal drücken, um sie zu erstellen. Das lässt sich nicht rückgängig machen.",
  },
  "breakdown.cancel": { en: "Cancel", de: "Abbrechen" },
  // F15: park/unpark strip the accept token as their first act, by design,
  // and the methodology re-run that re-mints it is async. Without
  // this, a fully-covered card in that window rendered a silently disabled
  // button with no explanation — indistinguishable from broken.
  "breakdown.confirm.tokenPending": {
    en: "One moment — Loom is re-checking this split before you can create it.",
    de: "Einen Moment — Loom prüft diese Aufteilung nochmal, bevor du sie erstellen kannst.",
  },
  // This card sent a change and the server has not answered. The one state in
  // which the rows on screen may be about to be replaced — so it is the only
  // state that may hold the commit, and the caption says which one it is
  // instead of leaving a greyed button next to a promise it cannot keep.
  "breakdown.confirm.opPending": {
    en: "Applying your change — one moment.",
    de: "Deine Änderung wird übernommen — einen Moment.",
  },
  // The accept frame is out and the server is writing. Held as its own state
  // because the write is explicitly no-undo: the row used to fall back to the
  // un-armed "Create N sub-issues" for the whole in-flight window, so a second
  // press was a plausible double-commit on an irreversible action.
  "breakdown.committing": {
    en: "Creating the sub-issues…",
    de: "Sub-Issues werden erstellt…",
  },
  "breakdown.committing.caption": {
    en: "Sent — Loom is creating them now. This can take a few seconds.",
    de: "Gesendet — Loom erstellt sie jetzt. Das kann ein paar Sekunden dauern.",
  },
  // A CLOSED session (the idle timer hard-closed it, or the server refused it).
  // Every control on this card sends a frame nothing will answer, so they are
  // disabled and this says why — live, the whole card stayed enabled under the
  // "Session closed" banner, the header still read "thinking…", and Confirm was
  // a silent no-op next to a caption promising a re-check that could never come.
  "breakdown.sessionClosed.chip": {
    en: "session closed",
    de: "Sitzung geschlossen",
  },
  "breakdown.confirm.sessionClosed": {
    en: "This session is closed, so nothing can be created from here. Start a new session — this split is saved and comes back with it.",
    de: "Diese Sitzung ist geschlossen — von hier lässt sich nichts erstellen. Starte eine neue Sitzung; diese Aufteilung ist gespeichert und kommt damit zurück.",
  },
  "breakdown.review.noSplit": {
    en: "One ticket suffices: {rationale}",
    de: "Ein Ticket genügt: {rationale}",
  },
  "breakdown.confirm": {
    en: "Create {count} sub-issues",
    de: "{count} Sub-Issues erstellen",
  },
  "breakdown.decline": { en: "Keep as one ticket", de: "Als ein Ticket behalten" },
  "breakdown.adjust.send": { en: "Send", de: "Senden" },
  "breakdown.child.remove": { en: "Remove", de: "Entfernen" },
  "breakdown.child.rename": { en: "Rename sub-issue", de: "Sub-Issue umbenennen" },
  // The rename used to commit on blur — invisible, and the gesture that landed
  // a newline in a GitLab title (type, Enter, click away). A named control.
  "breakdown.child.renameSave": { en: "Save the name", de: "Namen speichern" },
  // An empty name used to leave the save button enabled: the click no-op'd, the
  // editor closed, and the old name came back with nothing said. A rename that
  // cannot happen says so instead of looking like one that silently reverted.
  "breakdown.child.renameEmpty": {
    en: "A name is required — type one, or press Esc to keep the old one.",
    de: "Ein Name ist nötig — tippe einen ein oder drücke Esc, um den alten zu behalten.",
  },
  "breakdown.child.merge": { en: "Merge…", de: "Zusammenführen…" },
  "breakdown.child.mergeLabel": {
    en: "Merge this sub-issue into another",
    de: "Dieses Sub-Issue in ein anderes zusammenführen",
  },
  "breakdown.child.mergeInto": { en: "Merge into {n}", de: "In {n} zusammenführen" },

  // Remove flow. The old Remove button decided, silently, that the work moves
  // somewhere else ("fold its scope into the others"). These strings are the
  // question that replaced that decision.
  "breakdown.remove.title": {
    en: "Remove “{title}”",
    de: "„{title}“ entfernen",
  },
  "breakdown.remove.where": {
    en: "Where should this work go? Only this piece delivers these must-haves.",
    de: "Wohin gehört diese Arbeit? Nur dieser Teil liefert diese Muss-Kriterien.",
  },
  "breakdown.remove.noOrphans": {
    en: "Every must-have this piece delivers is also covered elsewhere, so nothing is lost.",
    de: "Alle Muss-Kriterien dieses Teils sind auch anderswo abgedeckt — es geht nichts verloren.",
  },
  "breakdown.remove.destinationFor": {
    en: "Where does “{text}” go?",
    de: "Wohin gehört „{text}“?",
  },
  "breakdown.remove.park": {
    en: "Don’t build it — drop from the must-haves",
    de: "Gar nicht bauen — aus den Muss-Kriterien streichen",
  },
  "breakdown.remove.parkDisabled": {
    en: "This issue is already written, so its must-haves can’t be changed here any more — the work can only move to another piece.",
    de: "Dieses Issue ist bereits geschrieben — die Muss-Kriterien lassen sich hier nicht mehr ändern. Die Arbeit kann nur zu einem anderen Teil verschoben werden.",
  },
  // The same fact, said where the missing control is. The line above lives
  // INSIDE the Remove dialog, so the user who noticed that every "Don't build
  // this" had vanished had no way to reach the explanation for it.
  "breakdown.parkDisabled.note": {
    en: "This issue is already written, so must-haves can’t be dropped here any more. Pieces can still be renamed, merged or split, and work can move between them.",
    de: "Dieses Issue ist bereits geschrieben — Muss-Kriterien lassen sich hier nicht mehr streichen. Teile lassen sich weiterhin umbenennen, zusammenführen oder aufteilen, und Arbeit kann zwischen ihnen wechseln.",
  },
  "breakdown.remove.strands": {
    en: "That would leave {titles} with nothing to deliver.",
    de: "Damit hätte {titles} nichts mehr zu liefern.",
  },
  "breakdown.remove.confirm": { en: "Remove piece", de: "Teil entfernen" },

  // Out-of-scope work. Shown as reversible on purpose: “drop EntraID” usually
  // means “not this sprint”.
  "breakdown.criterion.drop": {
    en: "Don’t build this",
    de: "Nicht bauen",
  },
  "breakdown.parked.label": {
    en: "Out of scope",
    de: "Nicht im Umfang",
  },
  "breakdown.parked.restore": { en: "Bring back…", de: "Zurückholen…" },
  "breakdown.parked.restoreInto": {
    en: "Bring “{text}” back into which piece?",
    de: "„{text}“ in welchen Teil zurückholen?",
  },
  // Op refusals, keyed by the server's machine code. The server sends the CODE;
  // the copy is chosen here, because a server-composed English sentence
  // rendered into a German UI is a regression the localized fallback was
  // hiding. `breakdown.op.failed` stays the catch-all for a code this build
  // does not know (deploy skew).
  "breakdown.op.failed": {
    en: "That change couldn’t be applied.",
    de: "Diese Änderung liess sich nicht anwenden.",
  },
  "breakdown.op.err.orphan_criteria": {
    en: "Some must-haves are only covered by that piece — say where they should go first.",
    de: "Einige Muss-Kriterien deckt nur dieser Teil ab — sag zuerst, wohin sie gehören.",
  },
  // F12: this sweep runs over EVERY piece, not just the one the user is
  // looking at — parking a criterion shared with a sibling can empty that
  // sibling instead. "another" used to be asserted unconditionally, which was
  // backwards in the commonest case (dropping a piece's own only must-have
  // empties exactly the piece the user just clicked). Kept piece-neutral here;
  // `empty_child.selfHint` below names the piece when the client already knows
  // it's this one.
  "breakdown.op.err.empty_child": {
    en: "That would leave a piece with nothing to deliver — every piece needs at least one must-have.",
    de: "Damit hätte ein Teil nichts mehr zu liefern — jeder Teil braucht mindestens ein Muss-Kriterium.",
  },
  "breakdown.op.err.empty_child.selfHint": {
    en: "“{title}” only has this one must-have — parking it would leave that piece empty.",
    de: "„{title}“ hat nur dieses eine Muss-Kriterium — es zu parken würde diesen Teil leer lassen.",
  },
  "breakdown.op.err.empty_child.selfHintRemove": {
    // Both quote the DIALOG option (`breakdown.remove.park`) verbatim, not the
    // per-criterion button the user just pressed — in German those differ by
    // one word ("Gar nicht bauen" vs "Nicht bauen"), and naming the failed
    // button as the way out reads as "do the thing that just didn't work".
    en: "To drop the whole piece instead, use its ⋯ menu → Remove → “Don’t build it — drop from the must-haves”.",
    de: "Um stattdessen den ganzen Teil zu verwerfen: dessen ⋯-Menü → Entfernen → „Gar nicht bauen — aus den Muss-Kriterien streichen“.",
  },
  // Both bounds, because both are now reachable from the card: Remove hits the
  // floor of two, and "+ Add a piece" — a real sender since the structured add
  // exists — hits the ceiling the sizing policy set.
  "breakdown.op.err.child_count_out_of_bounds": {
    en: "A split needs at least two pieces, and no more than this issue’s size allows.",
    de: "Eine Aufteilung braucht mindestens zwei Teile — und nicht mehr, als die Grösse dieses Issues zulässt.",
  },
  "breakdown.op.err.parent_already_applied": {
    en: "This issue has already been written — its must-haves can’t be changed here any more.",
    de: "Dieses Issue wurde bereits geschrieben — die Muss-Kriterien lassen sich hier nicht mehr ändern.",
  },
  "breakdown.op.err.coverage_gap": {
    en: "That would leave a must-have out of every piece.",
    de: "Damit wäre ein Muss-Kriterium in keinem Teil mehr enthalten.",
  },
  "breakdown.op.err.breakdown_op_stale": {
    en: "The split changed while you were looking at it — here’s the current one.",
    de: "Die Aufteilung hat sich geändert, während du sie angesehen hast — hier ist die aktuelle.",
  },
  "breakdown.op.err.unknown_child_id": {
    en: "The split changed while you were looking at it — here’s the current one.",
    de: "Die Aufteilung hat sich geändert, während du sie angesehen hast — hier ist die aktuelle.",
  },
  "breakdown.op.err.breakdown_locked": {
    en: "This split is already being created.",
    de: "Diese Aufteilung wird bereits erstellt.",
  },
  "breakdown.op.err.criteria_write_refused": {
    en: "That change to the must-haves couldn’t be recorded.",
    de: "Diese Änderung an den Muss-Kriterien liess sich nicht festhalten.",
  },
  "breakdown.op.err.no_sandbox": {
    en: "Loom’s workspace isn’t available right now — try that change again in a moment.",
    de: "Looms Arbeitsumgebung ist gerade nicht verfügbar — versuch die Änderung gleich nochmal.",
  },
  "breakdown.op.err.child_not_addressable": {
    en: "This split was made by an older version — describe the change instead.",
    de: "Diese Aufteilung stammt aus einer älteren Version — beschreib die Änderung stattdessen.",
  },
  "breakdown.op.err.merge_into_self": {
    en: "A piece can't be merged into itself — pick a different one.",
    de: "Ein Teil lässt sich nicht mit sich selbst zusammenführen — wähle ein anderes.",
  },
  "breakdown.op.err.criteria_moved": {
    en: "The must-haves changed since this split was drafted — ask for a fresh split.",
    de: "Die Muss-Kriterien haben sich seit dieser Aufteilung geändert — fordere eine neue Aufteilung an.",
  },
  "breakdown.op.err.ambiguous_parked_criterion": {
    en: "That out-of-scope item was named more than once — bring back one at a time.",
    de: "Dieser nicht im Umfang stehende Punkt wurde mehrfach genannt — hol jeweils nur einen zurück.",
  },
  "breakdown.op.err.unknown_parked_criterion": {
    en: "That item is no longer in the out-of-scope list — the card may have changed since.",
    de: "Dieser Punkt steht nicht mehr auf der Nicht-im-Umfang-Liste — die Karte hat sich inzwischen möglicherweise geändert.",
  },
  "breakdown.op.err.child_id_collision": {
    en: "Couldn't add that piece — try again.",
    de: "Dieser Teil liess sich nicht hinzufügen — versuch es erneut.",
  },
  "breakdown.creating": {
    en: "Creating {done} of {total}…",
    de: "Erstelle {done} von {total}…",
  },
  "breakdown.creating.queued": { en: "queued", de: "wartet" },
  "breakdown.creating.inflight": { en: "creating", de: "wird erstellt" },
  "breakdown.done.title": {
    en: "Split into {count} sub-issues",
    de: "In {count} Sub-Issues aufgeteilt",
  },
  "breakdown.done.seeLinks": {
    en: "See the Sub-issues group in the links panel below.",
    de: "Siehe die Sub-Issues-Gruppe im Verknüpfungsbereich unten.",
  },
  "breakdown.failed": {
    en: "Created {created} of {total} — retry continues where it left off.",
    de: "{created} von {total} erstellt — Wiederholen setzt dort fort.",
  },
  "breakdown.failed.title": {
    en: "Couldn't create the sub-issues",
    de: "Sub-Issues konnten nicht erstellt werden",
  },
  "breakdown.failed.stale": {
    en: "The issue changed since this split was drafted — reload the page, then split again.",
    de: "Das Issue hat sich seit dem Entwurf geändert — lade die Seite neu und teile erneut auf.",
  },
  "breakdown.failed.reason": {
    en: "Reason: {error}",
    de: "Grund: {error}",
  },
  "breakdown.failed.detail": {
    en: "Show technical detail",
    de: "Technische Details anzeigen",
  },
  "breakdown.retry": { en: "Retry", de: "Wiederholen" },
  "breakdown.unavailable": {
    en: "Couldn't draft a split — continuing with one ticket.",
    de: "Aufteilung nicht möglich — es bleibt bei einem Ticket.",
  },
  "breakdown.epicCrumb": { en: "Part of #{iid} {title}", de: "Teil von #{iid} {title}" },
  "breakdown.subIssuesGroup": { en: "Sub-issues", de: "Sub-Issues" },
  // Honest label: the count is closed-vs-total (link rows carry only open/closed
  // state, not the board's label-derived stage), so a merged-MR-but-open child
  // would otherwise read as "not shipped". Say what's actually measured.
  "breakdown.subIssuesProgress": {
    en: "{done} of {total} closed",
    de: "{done} von {total} geschlossen",
  },

  // Chat's synthetic-seed labels (R2-V). These belong to `focus/FocusChat.tsx`
  // by SUBJECT, not to breakdown — they live in this file because that's the
  // one i18n section the Focus-chat lane owns; `i18n/messages/focus.ts`
  // belongs to a different lane. The flat catalog (`messages/index.ts`)
  // doesn't care which file a key is declared in, only that the string is
  // unique.
  "focus.chat.seedLabel": {
    en: "Loom's brief for this issue",
    de: "Loom-Auftrag für dieses Issue",
  },
  "focus.chat.seedDetail": {
    en: "Show what was sent to Loom",
    de: "Zeigen, was an Loom gesendet wurde",
  },
};
